import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { getEventTypeConfig, formatEventDateTime } from '../utils/eventTypes';
import { getOpenAssignments } from '../utils/eventUtils';
import OpenAssignmentDisplay from '../details/OpenAssignmentDisplay';
import EventHeader from '../../../components/ui/events/EventHeader';
import EventDetails from '../details/EventDetails';
import EventNotesSection from '../details/EventNotesSection';
import EventImageModal from '../details/EventImageModal';
import ClusterAnalysis from '../details/ClusterAnalysis';
import DetectionImageCanvas from '../details/DetectionImageCanvas';
import { useEventNotes } from '../../../hooks/useEventNotes';
import { useEventSpeech } from '../../../hooks/useEventSpeech';
import { getCapturedImageDisplayUrl } from '../../../services/s3/captureUrlService';
import { getClusterAnalysis } from '../../../services/api/eventService';
import { CognitoUserContext } from '../../../contexts/cognitoUserContext';
import { getTenantId, getPersonImageUrl } from '../../../config/tenantConfig';

/**
 * EventActionDialog - Shows event details with edit and delete options
 * @param {boolean} open - Whether the dialog is open
 * @param {Object} event - Event to show actions for
 * @param {Array} allEvents - All events (to find open assignments)
 * @param {Function} onClose - Callback when dialog is closed
 * @param {Function} onEdit - Callback when edit is clicked
 * @param {Function} onDelete - Callback when delete is clicked
 */
export default function EventActionDialog({
  open,
  event,
  allEvents = [],
  onClose,
  onEdit,
  onDelete,
  onCreateStatement
}) {
  const { cognitoUser } = React.useContext(CognitoUserContext);
  const tenantId = getTenantId(cognitoUser);

  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [snackbarSeverity, setSnackbarSeverity] = React.useState('success');
  const [imageModalOpen, setImageModalOpen] = React.useState(false);
  const [imageModalSrc, setImageModalSrc] = React.useState('');

  // Snackbar helper
  const showSnackbar = React.useCallback((message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  // Custom hooks for managing complex state
  const {
    noteText,
    setNoteText,
    saving,
    editingNoteIndex,
    existingNotes,
    handleEditNote,
    handleCancelEdit,
    handleDeleteNote,
    handleSaveNote
  } = useEventNotes(event, onClose, showSnackbar);

  const {
    isListening,
    setIsNoteFocused,
    handleStopListening
  } = useEventSpeech(noteText, setNoteText, showSnackbar, open);

  // Cluster analysis state
  const [clusterData, setClusterData] = React.useState(null);
  const [clusterLoading, setClusterLoading] = React.useState(false);
  const [clusterError, setClusterError] = React.useState(null);

  // Fetch cluster analysis for detection events
  React.useEffect(() => {
    const isDetectionEvent = event?.eventType === 'detection';
    if (open && isDetectionEvent && event?.eventId) {
      setClusterLoading(true);
      setClusterError(null);
      getClusterAnalysis(event.eventId)
        .then(data => {
          setClusterData(data);
          setClusterLoading(false);
        })
        .catch(err => {
          console.error('[ClusterAnalysis] Failed to fetch:', err);
          setClusterError(err);
          setClusterLoading(false);
        });
    }
  }, [open, event?.eventType, event?.eventId]);

  // DEBUG: Log personImage for all match events (must be before early return)
  React.useEffect(() => {
    if (open && event) {
      const isMatchEvent = event.eventType === 'match';
      const isSOIMatchEvent = event.eventType === 'soi_match';
      if (isMatchEvent || isSOIMatchEvent) {
        console.log('[EventActionDialog] Match event personImage:', {
          ev: event,
          eventType: event.eventType,
          eventId: event.eventId,
          personName: event.metadata?.personName,
          personImage: event.metadata?.personImage,
          hasPersonImage: !!event.metadata?.personImage,
          matchType: event.metadata?.matchType,
          fullMetadata: event.metadata
        });
      }
    }
  }, [open, event]);

  if (!event) return null;

  const capturedImageSignedUrl = getCapturedImageDisplayUrl(event?.metadata?.capturedImageUrl);
  const config = getEventTypeConfig(event.eventType);
  const dateTimeStr = formatEventDateTime(event.createdAt);
  const isMatchEvent = event.eventType === 'match';
  const isSOIMatchEvent = event.eventType === 'soi_match';
  const isMultiDatasetMatchEvent = event.eventType === 'multi_dataset_match';
  const isGroupMatchEvent = event.eventType === 'group_match';
  const isDetectionEvent = event.eventType === 'detection';
  const isNoteEvent = event.eventType === 'note';
  const isPromotionEvent = event.eventType === 'promotion';
  const personImage = event.metadata?.personImage;
  const personName = event.metadata?.personName;
  const matchSource = event.metadata?.matchSource;
  const openAssignments = getOpenAssignments(event, allEvents);

  // Handler to toggle between captured and reference images
  const handleToggleImage = () => {
    setImageModalSrc(prev =>
      prev === capturedImageSignedUrl ? personImage : capturedImageSignedUrl
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Event Details</DialogTitle>
      <DialogContent>
        <Box>
          {(isMatchEvent || isSOIMatchEvent) && (
            <EventHeader
              personImage={personImage}
              personName={personName}
              headline={event.metadata?.person?.headline}
              matchSource={matchSource}
              capturedImageSignedUrl={capturedImageSignedUrl}
              capturedImageUrl={event.metadata?.capturedImageUrl}
              matchAccuracy={event.metadata?.matchAccuracy}
              eventLabel={config.label}
              personUri={event.metadata?.person?.uri}
              matchCount={event.metadata?.matchCount}
              detectedFaceCount={event.metadata?.detectedFaceCount}
              boundingBox={event.metadata?.boundingBox}
              interestLevel={event.metadata?.person?.interest?.level}
              interestReason={event.metadata?.person?.interest?.reason}
              onPersonImageClick={() => {
                setImageModalSrc(personImage);
                setImageModalOpen(true);
              }}
              onCapturedImageClick={() => {
                setImageModalSrc(capturedImageSignedUrl);
                setImageModalOpen(true);
              }}
            />
          )}

          {isMultiDatasetMatchEvent && (
            <Box sx={{ mb: 2 }}>
              {/* Captured image */}
              <Box
                component="img"
                src={capturedImageSignedUrl || ''}
                alt="Captured image"
                onClick={() => {
                  setImageModalSrc(capturedImageSignedUrl);
                  setImageModalOpen(true);
                }}
                sx={{
                  width: '100%',
                  maxWidth: 300,
                  borderRadius: 1,
                  objectFit: 'contain',
                  border: '2px solid',
                  borderColor: 'secondary.main',
                  cursor: 'pointer',
                  mb: 1.5,
                  '&:hover': { opacity: 0.8 }
                }}
              />

              {/* Person name and datasets */}
              <Typography variant="h6" gutterBottom>
                {personName}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Matched in {event.metadata?.datasetCount || 0} datasets: {event.metadata?.datasets?.join(', ')}
              </Typography>

              {/* Dataset images */}
              {event.metadata?.personImages && event.metadata.personImages.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                    Matches by Dataset
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {event.metadata.personImages.map((img, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          backgroundColor: '#f3e5f5'
                        }}
                      >
                        {img.imageUrl && (
                          <Box
                            component="img"
                            src={img.imageUrl}
                            alt={img.dataset}
                            onClick={() => {
                              setImageModalSrc(img.imageUrl);
                              setImageModalOpen(true);
                            }}
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: 1,
                              objectFit: 'cover',
                              border: '2px solid',
                              borderColor: 'secondary.main',
                              cursor: 'pointer',
                              flexShrink: 0,
                              '&:hover': { opacity: 0.8 }
                            }}
                          />
                        )}
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {img.dataset}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {img.externalImageId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Similarity: {img.similarity?.toFixed(1)}%
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {isGroupMatchEvent && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                component="img"
                src={capturedImageSignedUrl || ''}
                alt="Group photo"
                onClick={() => {
                  setImageModalSrc(capturedImageSignedUrl);
                  setImageModalOpen(true);
                }}
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: 1,
                  objectFit: 'cover',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8
                  }
                }}
              />
              <Box>
                <Typography variant="h6">
                  {event.metadata?.matchCount}/{event.metadata?.detectedFaceCount} faces matched
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {event.metadata?.personNames?.join(', ')}
                </Typography>
              </Box>
            </Box>
          )}

          {isPromotionEvent && (
            <Box sx={{ mb: 2 }}>
              {/* Captured image */}
              <Box
                component="img"
                src={capturedImageSignedUrl || ''}
                alt="Captured image"
                onClick={() => {
                  setImageModalSrc(capturedImageSignedUrl);
                  setImageModalOpen(true);
                }}
                sx={{
                  width: '100%',
                  maxWidth: 300,
                  borderRadius: 1,
                  objectFit: 'contain',
                  border: '2px solid',
                  borderColor: 'secondary.main',
                  cursor: 'pointer',
                  mb: 1.5,
                  '&:hover': { opacity: 0.8 }
                }}
              />

              {/* Person details */}
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                {/* Associated Person */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    New Person
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: '#f3e5f5' }}>
                    {event.metadata?.croppedFaceUrl && (
                      <Box
                        component="img"
                        src={event.metadata.croppedFaceUrl}
                        alt="Associated person"
                        sx={{ width: 48, height: 48, borderRadius: 1, objectFit: 'cover', border: '1px solid', borderColor: 'secondary.main', flexShrink: 0 }}
                      />
                    )}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {event.metadata?.associatedPersonId || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {event.metadata?.confidence?.toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Known Persons Nearby */}
                {event.metadata?.knownPersonsNearby && event.metadata.knownPersonsNearby.length > 0 && (
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                      Known Person Nearby
                    </Typography>
                    {event.metadata.knownPersonsNearby.map((kp, idx) => {
                      const person = kp.person;
                      const personName = person
                        ? `${person.first_name || ''} ${person.last_name || ''}`.trim() || kp.person_id
                        : kp.person_id;

                      const personImageUrl = getPersonImageUrl(person?.image, tenantId);

                      return (
                        <Box
                          key={idx}
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: idx < event.metadata.knownPersonsNearby.length - 1 ? 0.5 : 0 }}
                        >
                          {personImageUrl && (
                            <Box
                              component="img"
                              src={personImageUrl}
                              alt={personName}
                              sx={{ width: 48, height: 48, borderRadius: 1, objectFit: 'cover', border: '1px solid', borderColor: 'primary.main', flexShrink: 0 }}
                            />
                          )}
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {personName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              {kp.proximity}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>
          )}

          <EventDetails
            isMatchEvent={isMatchEvent}
            isMultiDatasetMatchEvent={isMultiDatasetMatchEvent}
            isNoteEvent={isNoteEvent}
            eventLabel={config.label}
            description={event.description}
            dateTimeStr={dateTimeStr}
            matchSource={matchSource}
            location={event.location}
            interestReason={event.metadata?.person?.interest?.reason || event.metadata?.persons?.[0]?.interest?.reason}
          />

          {isDetectionEvent && (
            <>
              {/* Visual display with bounding boxes */}
              {event.metadata?.allFaceDetails && (
                <DetectionImageCanvas
                  imageUrl={capturedImageSignedUrl}
                  detectedFaces={event.metadata.allFaceDetails}
                  onFaceClick={(face, index) => {
                    console.log('Face clicked:', face, index);
                    // TODO: Could open face detail dialog or navigate to person
                  }}
                />
              )}

              {/* Legacy cluster analysis (keep as accordion for detailed view) */}
              <ClusterAnalysis
                clusterData={clusterData}
                loading={clusterLoading}
                error={clusterError}
              />
            </>
          )}

          {/* Display witness statements for this person */}
          {(isMatchEvent || isSOIMatchEvent) && event.metadata?.person?.statements?.length > 0 && (
            <Box sx={{ mt: 1.5, p: 1, backgroundColor: '#f5f5f5', borderRadius: 0.5, border: '1px solid #e0e0e0' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.75, color: '#9c27b0', fontSize: '0.75rem', display: 'block' }}>
                Witness Statements ({event.metadata.person.statements.length})
              </Typography>
              {event.metadata.person.statements.map((stmt, idx) => (
                <Box
                  key={idx}
                  sx={{
                    mb: idx < event.metadata.person.statements.length - 1 ? 0.75 : 0,
                    pb: idx < event.metadata.person.statements.length - 1 ? 0.75 : 0,
                    borderBottom: idx < event.metadata.person.statements.length - 1 ? '1px solid #e0e0e0' : 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: 'monospace', color: '#9c27b0', fontSize: '0.7rem' }}>
                      {stmt.statement_id}
                    </Typography>
                    <Box
                      sx={{
                        px: 0.5,
                        py: 0.125,
                        borderRadius: 0.25,
                        backgroundColor: stmt.status === 'finalized' ? '#4caf50' : stmt.status === 'submitted' ? '#2196f3' : '#ff9800',
                        color: 'white',
                        fontSize: '0.5rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        lineHeight: 1.2
                      }}
                    >
                      {stmt.status}
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2, fontSize: '0.65rem' }}>
                    Witness: {stmt.witness?.handle || 'Unknown'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2, fontSize: '0.65rem' }}>
                    {new Date(stmt.created_at).toLocaleDateString()}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {isMatchEvent && <OpenAssignmentDisplay openAssignments={openAssignments} />}

          {(isMatchEvent || isNoteEvent) && (
            <EventNotesSection
              noteText={noteText}
              onNoteTextChange={setNoteText}
              onSave={handleSaveNote}
              onCancel={handleCancelEdit}
              saving={saving}
              isEditing={editingNoteIndex !== null}
              isListening={isListening}
              onStopListening={handleStopListening}
              onFocus={() => setIsNoteFocused(true)}
              onBlur={() => setIsNoteFocused(false)}
              notes={existingNotes}
              editingNoteIndex={editingNoteIndex}
              onEditNote={handleEditNote}
              onDeleteNote={handleDeleteNote}
              borderColor={isNoteEvent ? 'rgba(255, 152, 0, 0.5)' : undefined}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {/* Show Create Witness Statement button for events with captures */}
        {!isNoteEvent && event.metadata?.capturedImageUrl && onCreateStatement && (
          <Button
            onClick={() => onCreateStatement(event)}
            variant="contained"
            color="secondary"
          >
            Create Witness Statement
          </Button>
        )}
        {!isNoteEvent && (
          <Button onClick={onEdit} variant="outlined">Edit</Button>
        )}
        <Button onClick={onDelete} variant="outlined" color="error">
          Delete
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <EventImageModal
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        imageSrc={imageModalSrc}
        capturedImageUrl={capturedImageSignedUrl}
        personImage={personImage}
        onToggleImage={handleToggleImage}
      />
    </Dialog>
  );
}
